import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
import httpClient from "services/network/httpClient";
import { useEffect, useState } from "react";
import CustomSelect from "components/Select";
import { motion } from "framer-motion";
import CustomButton from "components/button/CustomButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faTimes } from "@fortawesome/free-solid-svg-icons";

export interface TotalSummaryFormProps {
    fromDate: string;
    toDate: string;
    manager: string;
    employee: string;
}

interface Option {
    label: string;
    value: number;
}

interface ManagerProps {
    id: number;
    firstName: string;
    lastName: string;
    employeeCode?: string
}

interface AssociatedEmployeesProps {
    id: number;
    firstName: string;
    lastName: string;
    employeeCode?: string
}
interface CropOrProduct {
    id: number
    name: string
}

export interface FilterFormValues extends TotalSummaryFormProps {
    product?: string;
    crop?: string;
    distributor?: string;
    status?: string;
}

interface DemoReportFilterProps {
    onClose: () => void
    title?: string
    onSubmit: (data: TotalSummaryFormProps) => void
    onReset?: () => void
    crops?: CropOrProduct[]
    products?: CropOrProduct[]
    showProductField?: boolean
    showCropField?: boolean
    showDistribuor?: boolean
    showStatus?: boolean
    showManager?: boolean
    showEmployee?: boolean
    initialValues?: Partial<FilterFormValues>
}

const convertToEpochStartOfDay = (dateStr: string) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0).getTime();
};

const convertToEpochEndOfDay = (dateStr: string) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day, 23, 59, 59).getTime();
};

const schema = yup.object({
    fromDate: yup.string().optional(),
    toDate: yup.string().optional(),
    manager: yup.string().optional(),
    employee: yup.string().optional(),
});

export const getDarkSelectStyles = (isDarkMode: boolean) => ({
    control: (base: any, state: any) => {
        const idleBorderColor = isDarkMode ? "#374151" : "#d1d5db";
        const borderColor = state.isFocused ? "#3b82f6" : idleBorderColor;
        return {
            ...base,
            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
            borderColor,
            boxShadow: "none",
            "&:hover": { borderColor: "#3b82f6" },
        };
    },
    menu: (base: any) => ({
        ...base,
        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
        zIndex: 9999,
    }),
    option: (base: any, state: any) => {
        const idleOptionBg = isDarkMode ? "#1f2937" : "#ffffff";
        const backgroundColor = state.isFocused ? "#3b82f6" : idleOptionBg;
        const idleColor = isDarkMode ? "#f3f4f6" : "#111827";
        const color = state.isFocused ? "#ffffff" : idleColor;
        return {
            ...base,
            backgroundColor,
            color,
            cursor: "pointer",
            height: "auto",
            overflow: "visible",
            whiteSpace: "normal",
            wordBreak: "break-word",
        };
    },
    singleValue: (base: any) => ({
        ...base,
        color: isDarkMode ? "#f9fafb" : "#111827",
        paddingLeft: "10px", // Add left padding for selected value
    }),
    placeholder: (base: any) => ({
        ...base,
        color: "#9ca3af",
        paddingLeft: "10px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
    }),
    input: (base: any) => ({
        ...base,
        color: isDarkMode ? "#f9fafb" : "#111827",
    }),
    indicatorsContainer: (base: any) => ({
        ...base,
        display: "flex",
        alignItems: "center",
        alignSelf: "stretch",
        padding: "0 8px",
    }),
    dropdownIndicator: (base: any) => ({
        ...base,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0",
        color: isDarkMode ? "#9ca3af" : "#6b7280",
        "&:hover": { color: isDarkMode ? "#f9fafb" : "#111827" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
});

const ALL_OPTION = { label: "All", value: "All" as unknown as number };

const DemoReportFilter = ({ onClose, title, onSubmit, onReset, crops = [], products = [], showCropField = false, showProductField = false, showDistribuor = false, showStatus = false, showManager = true, showEmployee = true, initialValues }: DemoReportFilterProps) => {

    const cropOptions = [ALL_OPTION, ...crops.map((c) => ({ value: c.id, label: c.name }))];
    const productOptions = [ALL_OPTION, ...products.map((p) => ({ value: p.id, label: p.name }))];

    const [listOfManager, setListOfManager] = useState<Option[]>([]);
    const [associatedEmployeeData, setAssociatedEmployeeData] = useState<Option[]>([]);
    const [selectedManager, setSelectedManager] = useState<Option>();
    const [selectedEmployee, setSelectedEmployee] = useState<Option>();
    const [selectedProduct, setSelectedProduct] = useState<Option>();
    const [selectedCrop, setSelectedCrop] = useState<Option>();
    const [selectedStatus, setSelectedStatus] = useState<Option>();

    // const today = new Date().toISOString().split("T")[0];
    const formatDate = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };
    const getLastDayOfMonth = () => {
        const now = new Date();
        return formatDate(
            new Date(now.getFullYear(), now.getMonth() + 1, 0) // ✅ key trick
        );
    };

    const today = getLastDayOfMonth();

    // const today = formatDate(new Date());

    const firstOfMonth = formatDate(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );

    const methods = useForm<TotalSummaryFormProps>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            fromDate: initialValues?.fromDate ?? firstOfMonth,
            toDate: initialValues?.toDate ?? today,
            manager: initialValues?.manager ?? "",
            employee: initialValues?.employee ?? "",
            ...(initialValues?.product !== undefined && { product: initialValues.product } as any),
            ...(initialValues?.crop !== undefined && { crop: initialValues.crop } as any),
            ...(initialValues?.distributor !== undefined && { distributor: initialValues.distributor } as any),
            ...(initialValues?.status !== undefined && { status: initialValues.status } as any),
        },
    });

    const { handleSubmit } = methods;

    // ---------- GET MANAGERS ----------
    const getManagerData = async () => {
        await httpClient.get("/employees/managers")
            .then((response: any) => {

                if (response.success) {
                    const managers = [ALL_OPTION, ...response.value.map((manager: ManagerProps) => ({
                        label: `${manager.employeeCode}/${manager.firstName} ${manager.lastName}`,
                        value: manager.id
                    }))];

                    setListOfManager(managers);
                }

            }).catch((error) => {
                console.error(error);
            });
    };

    // ---------- GET EMPLOYEES ----------
    const getAssociatedEmployees = async () => {

        const api = selectedManager
            ? `/employees/managers/${selectedManager.value}`
            : `/employees/all?page=1&size=2000`;

        await httpClient.get(api)
            .then((response: any) => {

                if (response.success) {

                    const employees = selectedManager
                        ? response.value
                        : response.value.contents;

                    const mapped = [ALL_OPTION, ...employees.map((emp: AssociatedEmployeesProps) => ({
                        label: `${emp.employeeCode ? emp.employeeCode + "/" : ""}${emp.lastName ? emp.firstName + " " + emp.lastName : emp.firstName}`,
                        value: emp.id
                    }))];

                    setAssociatedEmployeeData(mapped);
                }

            }).catch((error) => {
                console.error(error);
            });
    };

    useEffect(() => {
        getManagerData();
    }, []);

    // Restore selectedManager from loaded options when initialValues has a manager
    useEffect(() => {
        if (listOfManager.length > 0 && initialValues?.manager) {
            const match = listOfManager.find(m => String(m.value) === String(initialValues.manager));
            if (match) setSelectedManager(match);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listOfManager]);

    useEffect(() => {
        if (String(selectedManager?.value) === "All") {
            return;
        }
        getAssociatedEmployees();
    }, [selectedManager]);

    // ---------- FORM SUBMIT ----------


    const statusOptions: Option[] = [
        ALL_OPTION,
        {
            label: "Follow Up",
            value: 1
        },
        {
            label: "Completed",
            value: 0
        },
    ]

    const [isDarkMode, setIsDarkMode] = useState(
        () => document.documentElement.classList.contains("dark")
    );

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDarkMode(document.documentElement.classList.contains("dark"));
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    const handleReset = () => {
        methods.reset({
            fromDate: firstOfMonth,
            toDate: today,
            manager: "",
            employee: "",
            ...(showProductField && { product: "" } as any),
            ...(showCropField && { crop: "" } as any),
            ...(showDistribuor && { distributor: "" } as any),
            ...(showStatus && { status: "" } as any),
        });
        setSelectedManager(undefined);
        setSelectedEmployee(undefined);
        setSelectedProduct(undefined);
        setSelectedCrop(undefined);
        setSelectedStatus(undefined);
        if (onReset) onReset();
    }

    return (
        <FormProvider {...methods}>

            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 px-3 py-4 dark:border-gray-700  w-full max-w-3xl"
            >

                <form onSubmit={handleSubmit((data) => {
                    (data as any).__employeeLabel = selectedEmployee?.label;
                    (data as any).__managerLabel = selectedManager?.label;
                    (data as any).__productLabel = selectedProduct?.label;
                    (data as any).__cropLabel = selectedCrop?.label;
                    (data as any).__statusLabel = selectedStatus?.label;
                    // onSubmit(data);
                    onSubmit({
                        ...data,
                        fromDate: data.fromDate ? convertToEpochStartOfDay(data.fromDate) : "",
                        toDate: data.toDate ? convertToEpochEndOfDay(data.toDate) : ""
                    } as any);
                })} className="space-y-6">

                    <div className="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-700 dark:text-white">
                            <FontAwesomeIcon icon={faFilter} className="mr-2 text-blue-600 dark:text-blue-400" />
                            {title ?? "Generate Summary Report"}
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-6">

                        {/* FROM DATE */}
                        <div className="flex flex-col gap-1">

                            <Input name="fromDate" type="date" label="From Date" />
                        </div>

                        {/* TO DATE */}
                        <div className="flex flex-col gap-1">

                            <Input name="toDate" type="date" label='To Date' />
                        </div>

                        {/* MANAGER */}
                        {
                            showManager && (
                                <CustomSelect
                                    name="manager"
                                    label="Manager"
                                    placeholder="Manager"
                                    options={listOfManager}
                                    onChange={(value: Option) => setSelectedManager(value)}
                                    customStyles={getDarkSelectStyles(isDarkMode)}
                                />
                            )
                        }

                        {/* EMPLOYEE */}
                        {
                            showEmployee && (
                                <CustomSelect
                                    name="employee"
                                    label="Employee"
                                    placeholder="Employee"
                                    options={associatedEmployeeData}
                                    customStyles={getDarkSelectStyles(isDarkMode)}
                                />
                            )
                        }
                        {/** Products */}

                        {
                            showProductField &&
                            <CustomSelect
                                name="product"
                                label="Product"
                                placeholder="Product"
                                options={productOptions}
                                onChange={(value: Option | null) => setSelectedProduct(value ?? undefined)}
                                customStyles={getDarkSelectStyles(isDarkMode)}
                            />
                        }

                        {/** Crops */}

                        {
                            showCropField &&
                            <CustomSelect
                                name="crop"
                                label="Crop"
                                placeholder="Crop"
                                options={cropOptions}
                                onChange={(value: Option | null) => setSelectedCrop(value ?? undefined)}
                                customStyles={getDarkSelectStyles(isDarkMode)}
                            />
                        }
                        {
                            showDistribuor &&
                            <CustomSelect
                                name="distributor"
                                label="Distributor"
                                placeholder="Distributor"
                                options={cropOptions}
                                customStyles={getDarkSelectStyles(isDarkMode)}
                            />
                        }
                        {
                            showStatus &&
                            <CustomSelect
                                name="status"
                                label="Status"
                                placeholder="Status"
                                options={statusOptions}
                                onChange={(value: Option | null) => setSelectedStatus(value ?? undefined)}
                                customStyles={getDarkSelectStyles(isDarkMode)}
                            />
                        }

                    </div>

                    <div className="flex justify-end pt-2 gap-4">
                        {/* <button
                            type="button"
                            onClick={handleReset}
                            className="px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 font-medium transition-colors duration-200"
                        >
                            Reset
                        </button> */}
                        <CustomButton
                            variant="danger"
                            onClick={handleReset}
                        >Reset</CustomButton>
                        <CustomButton
                            onClick={onClose}
                        >Cancel</CustomButton>
                        <button
                            type="submit"
                            className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white border border-transparent rounded-lg px-6 py-2 font-semibold transition-colors duration-200"
                        >
                            Apply Filter
                        </button>
                    </div>

                </form>

            </motion.div>

        </FormProvider>
    );
};

export default DemoReportFilter;